import type { SrpTopEmployersStripItem } from '../../types/srp-top-employers-strip';

export type TopEmployersStripApiResponse = {
  employers: SrpTopEmployersStripItem[];
};

/**
 * Browser: loads the same hub top-employers list (Finland-market listings) as SSR when the page payload has no strip
 * (e.g. client-side navigation into the SRP).
 */
export async function fetchTopEmployersStripFromApi(signal?: AbortSignal): Promise<SrpTopEmployersStripItem[]> {
  try {
    const res = await fetch('/api/top-employers-strip', {
      method: 'GET',
      credentials: 'same-origin',
      signal,
    });
    if (!res.ok) return [];
    const json = (await res.json()) as TopEmployersStripApiResponse;
    return Array.isArray(json?.employers) ? json.employers : [];
  } catch {
    return [];
  }
}

/** Session cache so repeat SRP visits and concurrent callers share one network request. */
let memoryCache: SrpTopEmployersStripItem[] | null = null;
let inflight: Promise<SrpTopEmployersStripItem[]> | null = null;

/**
 * Single-flight + memory cache for the top-employers strip. Prefer this over {@link fetchTopEmployersStripFromApi}
 * for UI so navigations do not refetch or race duplicate requests.
 */
export function loadTopEmployersStripDeduped(): Promise<SrpTopEmployersStripItem[]> {
  if (memoryCache !== null) {
    return Promise.resolve(memoryCache);
  }
  if (inflight) {
    return inflight;
  }
  inflight = fetchTopEmployersStripFromApi()
    .then((employers) => {
      memoryCache = employers;
      return employers;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}
